import { cn } from "@/libs/utils";
import Image from "next/image";
import { useTrans } from "@/utils/useTrans";
import { Button } from "@/v2-components/ui/button";
import useUser from "@/api/apis";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/v2-components/ui/dialog";
import { TextArea } from "@/v2-components/ui/textarea";
import { SwitchCase } from "@/v2-components/switch-case";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { Checkbox } from "@/v2-components/ui/checkbox";
import { Label } from "@/v2-components/ui/label";
import CrossIcon from "@/v2-components/core/icon/cross-icon";
import { AuthUtils } from "@/utils/AuthUtils";
import { useRouter } from "next/router";
import {
  usePlayerFeedback,
  PlayerPlatform,
} from "@/v2-components/pages/software/components/usePlayerFeedback";
import { sendPlayerFeedbackEmail } from "@/v2-components/pages/software/components/playerFeedbackEmail";

export default function LeaveFeedback({
  className,
  imageSrc,
  mobileImageSrc,
  title = "Have a feature in mind?",
  platform,
}: {
  className?: string;
  imageSrc: string;
  mobileImageSrc: string;
  title?: string;
  platform: PlayerPlatform;
}) {
  const { t } = useTrans(["software"]);
  const [open, setOpen] = useState(false);
  const { user } = useUser();
  const router = useRouter();

  return (
    <>
      {open && (
        <OpinionDialog
          platform={platform}
          onOpenChange={() => setOpen(false)}
        />
      )}
      <div
        onClick={(e: any) => {
          if (!user) {
            e.preventDefault();
            router.push(AuthUtils.getLoginUrl());
            return;
          }
          setOpen(true);
        }}
        /**
         * ★**두 폭의 배치가 다르다** (시안 `29889:100510` 데스크탑 · `29942:16692` 모바일).
         *
         * ```
         *              모바일 328×374            데스크탑 1440×280
         *   배치        세로 — 이미지 위          가로 — 텍스트 좌 · 이미지 우
         *   이미지      328×240 (상단 전폭)       846×280 (우측)
         *   본문        p-24 · gap-24 · 가운데    좌 48 · gap-32 · **세로 중앙**
         * ```
         *
         * 종전엔 **두 폭 다 가로**였고 모바일 이미지가 우측 161×168 이었다.
         */
        className={cn(
          "relative flex flex-col overflow-hidden rounded-2xl bg-[#f2f4f7] tablet:flex-row",
          className,
        )}
      >
        {/* 모바일 전용 — 시안은 이미지가 **위**다(`29942:16693` 328×240).
            자산도 그 슬롯에 맞춰 교체했다(322×336 → 656×480 = 328×240 @2x). */}
        <div className="relative aspect-[328/240] w-full tablet:hidden">
          <Image
            alt={title || "image"}
            className="object-cover"
            src={mobileImageSrc}
            fill
            sizes="(min-width: 768px) 0px, 100vw"
          />
        </div>

        {/**
         * ★**데스크탑은 세로 중앙이다** — 시안에서 텍스트 묶음(108 = 36 + 32 + 40)이
         * `y=86` 에서 시작해 `86 + 54 = 140 = 280/2` 로 정확히 가운데다. 종전엔
         * `pt-9`(36) 로 위에 붙어 **50px 위**였다(실측 2026-09-07 dev 1440).
         * 갭도 36(`gap-y-9`) 이었는데 시안은 **32** 다.
         */}
        <div className="z-0 flex flex-col items-center gap-6 p-6 tablet:min-w-[300px] tablet:items-start tablet:justify-center tablet:gap-8 tablet:p-0 tablet:pl-12">
          <Title>{t(title)}</Title>
          <Button
            variant={"blue-fill"}
            shape={"pill"}
            size={"sm"}
            className="w-fit px-6 py-3"
          >
            <span className="font-600">{t("Suggest a Feature")}</span>
          </Button>
        </div>

        {/* 데스크탑 전용 이미지 — 우측 정렬. 시안 자산은 배경까지 포함한 1440 전폭이지만
            컨테이너가 1408 이라 그대로 쓰면 잘린다. 배경은 루트가 칠하고 사진만 얹는
            **기존 방식**을 유지한다(자산 교체 없이 같은 그림이 된다). */}
        <div className="relative hidden grow justify-end tablet:flex">
          <div className="relative aspect-[846/280] w-[846px]">
            <Image
              alt={title || "image"}
              className="object-contain object-left"
              src={imageSrc}
              fill
              sizes="(min-width: 768px) 846px, 0px"
            />
          </div>
        </div>
      </div>
    </>
  );
}

const Title = ({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) => {
  return (
    <h2
      /**
       * 시안: 모바일 16/22 Medium · 데스크탑 28/36 Regular — 토큰이 그대로 있다
       * (`text-lg` = 16/22, `text-4xl` = 28/36).
       *
       * ⚠️ **타입 전환점은 `laptop`(1024) 인데 배치 전환점은 `tablet`(768) 이다.**
       * 시안이 360·1440 두 장뿐이라 그 사이는 정의돼 있지 않다 — 이번엔 실측된 두
       * 폭만 맞추고 이 불일치는 그대로 뒀다(768~1023 은 가로 배치 + 16px 제목).
       */
      className={cn(
        "text-center text-lg font-500 text-coolgray-90 tablet:text-start laptop:text-4xl laptop:font-400",
        className,
      )}
    >
      {children}
    </h2>
  );
};

type Phase = "write" | "wrote";

export const OpinionDialog = ({
  platform,
  onOpenChange,
}: {
  platform: PlayerPlatform;
  onOpenChange?: (open: boolean) => void;
}) => {
  const { t } = useTrans(["games", "gnb"]);
  const [phase, setPhase] = useState<Phase>("write");
  const isMobileView = useIsMobileView();

  return (
    <Dialog onOpenChange={onOpenChange} open={true}>
      <DialogContent
        className={cn("overflow-y-auto laptop:max-w-[400px]")}
        fullScreen={isMobileView}
      >
        <DialogHeader className="flex min-h-16 items-center border-b border-coolgray-20 laptop:min-h-fit laptop:border-none">
          <DialogTitle className={cn("sr-only invisible laptop:not-sr-only")}>
            {t("We want to hear from you!")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            We want to hear from you!
          </DialogDescription>
          <DialogClose className="hidden laptop:block" />
          <div
            className={cn(
              "flex flex-1 laptop:hidden",
              phase !== "write" && "justify-end",
            )}
          >
            <DialogClose asChild>
              <button>
                <CrossIcon className="mx-4 h-6 w-6" />
              </button>
            </DialogClose>
          </div>
        </DialogHeader>

        <SwitchCase
          value={phase}
          caseBy={{
            write: <WritePhase platform={platform} setPhase={setPhase} />,
            wrote: <WrotePhase />,
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

const opinionSchema = yup.object({
  opinion: yup.string().max(1000).required(),
  needContact: yup.boolean(),
});

type OpinionSchema = yup.InferType<typeof opinionSchema>;

function WritePhase({
  platform,
  setPhase,
}: {
  platform: PlayerPlatform;
  setPhase: (phase: Phase) => void;
}) {
  const { user } = useUser();
  const { t } = useTrans(["games", "gnb"]);
  const playerFeedback = usePlayerFeedback();

  const form = useForm({
    resolver: yupResolver(opinionSchema),
    defaultValues: { opinion: "", needContact: false },
    mode: "onChange",
  });

  const submit = async (data: OpinionSchema) => {
    if (!user) return;

    const needContact = data.needContact ?? false;

    const saveFeedback = playerFeedback.mutateAsync({
      commentText: data.opinion,
      needContact,
      platform,
    });

    const sendContactEmail = needContact
      ? sendPlayerFeedbackEmail({
          email: user.email,
          userId: user.userId,
          platform,
          opinion: data.opinion,
        })
      : Promise.resolve();

    try {
      await Promise.all([saveFeedback, sendContactEmail]);
      setPhase("wrote");
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(submit)}>
      <div className="flex flex-col gap-5 px-4 laptop:px-0">
        <div className="flex flex-col gap-3">
          <h2 className="text-2xl">{t("We want to hear from you!")}</h2>
          <p className="text-base">
            {t("Please suggest a feature you’d like to see")}
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <TextArea
            placeholder={t(
              "Enter feedback or suggestion here. To help protect your privacy, don’t include personal info, like your address or phone number. ",
            )}
            className="min-h-[240px]"
            maxLength={1000}
            {...form.register("opinion")}
          />
          <p className="text-end text-base text-coolgray-40">{`${form.watch("opinion").length}/1000`}</p>
        </div>
      </div>

      <DialogFooter className="mb-16 flex-col justify-between gap-5 px-4 laptop:mb-0 laptop:flex-row laptop:items-center laptop:gap-2 laptop:px-0">
        <Label variant="checkbox" className="space-x-2">
          <Checkbox
            className="border-coolgray-50 data-[state=checked]:border-coolgray-90"
            {...form.register("needContact")}
            onCheckedChange={(check) => {
              form.setValue("needContact", check as boolean);
            }}
          />
          <span>{t("Contact me about my feedback")}</span>
        </Label>
        <Button
          size="sm"
          variant="blue-fill"
          shape="pill"
          className="w-full px-6 laptop:w-auto"
          disabled={!form.formState.isValid || form.formState.isSubmitting}
        >
          {t("Submit")}
        </Button>
      </DialogFooter>
    </form>
  );
}

function WrotePhase() {
  const { t } = useTrans(["games"]);
  return (
    <div className="px-4 laptop:px-0">
      <div className="flex flex-col items-center text-center">
        <div className="relative aspect-square w-[230px]">
          <Image
            src="/images/buzz.png"
            alt="thank you"
            fill
            className="absolute object-cover"
          />
        </div>
        <h2 className="text-4xl">
          {t("Thank you for sharing your valuable feedback!")}
        </h2>
        <p className="mt-4 text-lg">
          {t(
            "We will do our best to reflect your suggestions and make improvements.",
          )}
        </p>
      </div>

      <DialogFooter className="mb-16 justify-end gap-2 laptop:mb-0 laptop:px-0">
        <DialogClose asChild>
          <Button
            size="sm"
            variant="blue-fill"
            shape="pill"
            className="w-full px-7 laptop:w-auto"
          >
            {t("OK")}
          </Button>
        </DialogClose>
      </DialogFooter>
    </div>
  );
}
